import * as React from 'react';
import { connect } from 'react-redux';

import { ExcelUploadAccountingPackageNameId } from '../../Models/Api/AccountingPackage';
import { PortalTextAccountingSystems, PortalTextCommon } from '../../Models/Api/strongbox.financialportal';
import { ApplicationState } from '../../Store';
import { IConnectionError } from '../../Store/NewDataCollectionParameters';
import { GetEnabledAccountingPackagePresentations } from '../../Store/AppSettings';
import { GetPortalText } from '../../Store/PortalAssets';
import { StringFormat } from '../../Utils/StringUtils';
import { IAccountingPackagePresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';

import { faSpinnerThird } from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Overlay } from '../Overlay/Overlay';
import { Button, ModalHeader, Notification } from '../design-system/components';
import { DiscardProgressModal } from '../DiscardProgressModal/DiscardProgressModal';
import '../ImportCustomerFinancials/NewConnectionModal.scss';

type InjectedReduxState = {
    commonText: PortalTextCommon | undefined;
    portalTextAccountingSystem: PortalTextAccountingSystems | undefined;
    accountingPackageDescriptions: IAccountingPackagePresentation[]
};

type InjectedActionCreators = {};

type ConnectionConfirmationModalProps = {
    accountingSystem: string;
    // True when the workspace already has a reusable connection for this package. Drives the reuse-or-
    // reconnect variant of the modal; false renders the plain single-button "Connect" confirmation.
    hasExistingConnection: boolean;
    // When true the modal stays mounted but shows only a spinner in the footer (no buttons): the connect popup
    // has been opened and we are waiting on it. Keeping the SAME modal on screen — rather than swapping in a
    // separate "Waiting on connection…" modal — is what makes it "stay active with a spinner" and then cleanly
    // revert to its buttons if the connect does not succeed.
    connecting?: boolean;
    // When set, the connect attempt failed: the modal shows the connector's error message (in place of the
    // confirm content) with Cancel + Try again, so the failure surfaces in the SAME "Connect to {system}"
    // window the user was waiting in rather than a separate error modal. `connecting` takes precedence (a
    // fresh attempt is in flight); the store clears the error when the retry dispatches 'connecting'.
    connectionError?: IConnectionError;
    onConnect: () => void;
    // Proceed with the already-established connection ("Update financials now").
    onReuseConnection: () => void;
    // Disconnect/forget the existing connection; the flow then reverts to the plain "Connect" state.
    onDisconnect: () => void;
    onCancel: () => void;
    onBack: () => void;
};

type Props = ConnectionConfirmationModalProps & InjectedReduxState & InjectedActionCreators;

const ConnectionConfirmationModalComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        accountingSystem,
        hasExistingConnection,
        connecting,
        connectionError,
        commonText,
        portalTextAccountingSystem,
        accountingPackageDescriptions,
        onConnect,
        onReuseConnection,
        onDisconnect,
        onCancel,
    } = props;

    const [showDiscardConfirm, setShowDiscardConfirm] = React.useState<boolean>(false);

    const accountingPackageDescriptor: string = React.useMemo(() => {
        // The excel / external-file upload package isn't in the accounting-package presentations (it's a file
        // upload, not an OAuth system), so resolve its descriptor from portal text (excelFileUploadDescriptor).
        if (accountingSystem.toLowerCase() === ExcelUploadAccountingPackageNameId.toLowerCase()) {
            return commonText?.excelFileUploadDescriptor ?? '';
        }
        const accountingPackage = accountingPackageDescriptions.find(x => (x.featureName as string).toLowerCase() === accountingSystem.toLowerCase());
        return accountingPackage?.descriptor ?? '';
    }, [accountingPackageDescriptions, accountingSystem, commonText]);

    const lookupPortalAccountingSystemTextByKey = (keyValue: string): string => {
        if (!portalTextAccountingSystem) {
            // Shouldn't really happen but should be guarded.
            return 'Text used for display in this modal couldn\'t be retrieved. Text should be in blob storage under portalassets\\text\\accountingsystems';
        }
        const value = Object.keys(portalTextAccountingSystem).find(a => a.toLowerCase() === keyValue);
        if (!value) {
            return 'unknown';
        }
        return portalTextAccountingSystem[value];
    }

    const description: string = React.useMemo(() => {
        return lookupPortalAccountingSystemTextByKey(('linkModalDescription' + accountingSystem).toLowerCase());

        // This is the list of dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountingSystem, portalTextAccountingSystem]);

    const oneWayText: string = React.useMemo(() => {
        return lookupPortalAccountingSystemTextByKey(('linkModal' + accountingSystem + 'OneWay').toLowerCase());

        // This is the list of dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountingSystem, portalTextAccountingSystem]);

    const reuseDescription: string = React.useMemo(() => {
        return StringFormat(
            portalTextAccountingSystem?.reuseConnectionDescription ||
            "You're already connected to {0}. Re-import your latest financials using this connection, or reconnect to use different credentials.",
            accountingPackageDescriptor
        );
    }, [portalTextAccountingSystem, accountingPackageDescriptor]);

    const connectingToAccountingSystem: string = React.useMemo(() => {
        return StringFormat(`${commonText?.connectToAccountingPkg} {0}` || 'Connect to your accounting system', accountingPackageDescriptor);
    }, [accountingPackageDescriptor, commonText]);

    // While the connect popup is open, the modal turns into a "Waiting for authorization" state: the title
    // switches to "Connecting to {system}", the body becomes a centered spinner + waiting copy, and the
    // footer/close are removed (the user completes the authorization in the popup).
    const waitingTitle: string = React.useMemo(() => {
        return StringFormat(portalTextAccountingSystem?.templateConnectingToAccountingSystem || 'Connecting to {0}', accountingPackageDescriptor);
    }, [portalTextAccountingSystem, accountingPackageDescriptor]);

    const waitingDescription: string = React.useMemo(() => {
        return StringFormat(
            portalTextAccountingSystem?.waitingForAuthorizationDescription || 'Complete the authorization in the {0} window. This page will update automatically.',
            accountingPackageDescriptor
        );
    }, [portalTextAccountingSystem, accountingPackageDescriptor]);

    // Cancelling (footer Cancel or header X) prompts the "Discard progress?" confirmation; confirming runs the
    // real onCancel (aborts the flow). While connecting the X is hidden and the footer is a spinner, so the
    // confirmation is unreachable then.
    const requestClose = () => setShowDiscardConfirm(true);
    const onClose = connecting ? undefined : requestClose;

    // A connect attempt failed (and no fresh attempt is in flight): show the error message with Cancel /
    // Try again in place of the confirm content. `connecting` wins so a retry shows the spinner, not the
    // now-stale error.
    const showError = !connecting && !!connectionError;

    return (
        <>
        <Overlay>
            <div className={'cr-modal page-modal'} style={{ width: 600 }}>
                <ModalHeader title={connecting ? waitingTitle : connectingToAccountingSystem} hasCloseButton={!connecting} onClose={onClose} />
                <div className={'cr-modal-body page-modal-body'}>
                    {connecting
                        ? (
                            <div className={'page-centered-content page-centered-content--gap-medium'}>
                                <FontAwesomeIcon icon={faSpinnerThird} spin className={'page-large-icon'} />
                                <div className={'page-stack page-stack--tight'}>
                                    <span className={'page-section-label'} style={{ color: 'var(--text-color-primary-default)' }}>{portalTextAccountingSystem?.waitingForAuthorization || ''}</span>
                                    <span className={'page-body-text'} style={{ maxWidth: 360 }}>{waitingDescription}</span>
                                </div>
                            </div>
                        )
                        : showError
                        ? (
                            <div className={'page-stack page-stack--medium'}>
                                <Notification
                                    type={'danger'}
                                    layout={'banner'}
                                    heading={commonText?.error || 'Error'}
                                    description={connectionError?.detailedMsg || connectionError?.msg || ''}
                                    dismissable={false}
                                    // Match the info banner above: span the full body width.
                                    style={{ width: '100%' }}
                                />
                            </div>
                        )
                        : (
                            <div className={'page-stack page-stack--medium'}>
                                {hasExistingConnection
                                    ? (
                                        <p className={'page-body-text'}>{reuseDescription}</p>
                                    )
                                    : (
                                        <>
                                            <p className={'page-body-text'}>{description}</p>
                                            <Notification
                                                type={'info'}
                                                layout={'banner'}
                                                heading={''}
                                                description={oneWayText}
                                                dismissable={false}
                                                // Override the design-system notification's fixed 384px width so the banner
                                                // spans the full body width, matching the description paragraph above it.
                                                style={{ width: '100%' }}
                                            />
                                        </>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
                {/* No footer while connecting — the user completes the authorization in the popup (no cancel). */}
                {!connecting && showError && (
                    <div className={'cr-modal-footer'}>
                        <Button type={'floating'} onClick={requestClose}>{commonText?.cancel || 'Cancel'}</Button>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--size-gap-control-group)' }}>
                            <Button type={'primary'} className={'page-btn--min-width'} onClick={onConnect}>
                                {commonText?.retry || 'Try again'}
                            </Button>
                        </div>
                    </div>
                )}
                {!connecting && !showError && (
                    <div className={'cr-modal-footer'}>
                        {/* .cr-modal-footer is space-between, so the floating Cancel sits left (mirrors the
                            header X → onCancel) and the action button(s) sit right. */}
                        <Button type={'floating'} onClick={requestClose}>{commonText?.cancel || 'Cancel'}</Button>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--size-gap-control-group)' }}>
                            {hasExistingConnection
                                ? (
                                    <>
                                        <Button type={'secondary'} onClick={onDisconnect}>
                                            {commonText?.disconnectFromAccountingPkg || 'Disconnect'}
                                        </Button>
                                        <Button type={'primary'} onClick={onReuseConnection}>
                                            {commonText?.linkModalUpdateFinancialsNow || 'Update financials now'}
                                        </Button>
                                    </>
                                )
                                : (
                                    <Button type={'primary'} className={'page-btn--min-width'} onClick={onConnect}>
                                        {commonText?.connect || 'Connect'}
                                    </Button>
                                )
                            }
                        </div>
                    </div>
                )}
            </div>
        </Overlay>
        {showDiscardConfirm && (
            <DiscardProgressModal
                onKeepEditing={() => setShowDiscardConfirm(false)}
                onDiscard={() => {
                    setShowDiscardConfirm(false);
                    onCancel();
                }}
            />
        )}
        </>
    );
}

export const ConnectionConfirmationModal = connect<InjectedReduxState, InjectedActionCreators, ConnectionConfirmationModalProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            portalTextAccountingSystem: GetPortalText<PortalTextAccountingSystems>(appState, 'accountingSystems'),
            accountingPackageDescriptions: GetEnabledAccountingPackagePresentations(appState),
        };
        return result;
    },
    {}
)(ConnectionConfirmationModalComponent);
